import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, Mail, Search, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { useCounts } from '@/lib/api/hooks/useCounts';
import { useMediaQuery } from '@/lib/useMediaQuery';

import { Logo } from './Logo';
import { TopbarSearch } from './TopbarSearch';
import { TopbarPokes } from './TopbarPokes';
import { UserMenu } from './UserMenu';
import { DotBadge } from './_shared';

/**
 * Dashboard top bar: logo, live people search, quick actions, and the
 * account menu. The search input itself (with its typing-driven dropdown of
 * matching users) lives in `TopbarSearch`.
 *
 * Mobile (≤ 640px) variant:
 *   - The Mail / Messages icon is hidden — chat stays a tap deeper in nav.
 *   - A Search icon is added BEFORE Notifications. Tapping it swaps the
 *     topbar's contents for a full-width search field + an X close button.
 *   - Auto-closes if the viewport resizes to sm+ while the overlay is open.
 */
export function Topbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: counts } = useCounts();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // Auto-dismiss the mobile search overlay if the viewport grows past sm —
  // otherwise a phone-to-tablet rotation would leave the overlay stranded
  // over the desktop layout.
  const isSmUp = useMediaQuery('(min-width: 640px)');
  useEffect(() => {
    if (isSmUp && mobileSearchOpen) setMobileSearchOpen(false);
  }, [isSmUp, mobileSearchOpen]);

  // Close the mobile search overlay any time the route changes — e.g. when
  // the user picks a result inside `TopbarSearch` and `navigate()` fires.
  // Without this, the overlay state lives on in the persistent `Topbar` and
  // covers the destination page.
  useEffect(() => {
    setMobileSearchOpen(false);
  }, [pathname]);

  return (
    // The bg-brand band spans the full viewport width (the colour stays
    // edge-to-edge), but the inner row is `mx-auto max-w-7xl` so the
    // logo/search/icons line up with the dashboard's max-w-7xl content
    // column below it.
    <header className="sticky top-0 z-40 h-16 border-b border-brand-600 bg-brand text-white">
      <div className="mx-auto flex h-full max-w-7xl items-center gap-3 px-4 sm:px-6">
        {mobileSearchOpen ? (
          // Mobile-only overlay — `sm:hidden` guard is belt-and-suspenders
          // (the auto-close effect above already prevents the desktop case).
          <div className="flex w-full items-center gap-2 sm:hidden">
            <div className="flex-1">
              <TopbarSearch mobileOpen />
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Close search"
              className="shrink-0 text-white hover:bg-white/15 hover:text-white"
              onClick={() => setMobileSearchOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        ) : (
          <>
            <Logo />

            <TopbarSearch />

            <div className="ml-auto flex items-center gap-1 sm:gap-2">
              {/* Mobile-only search trigger — sits before Notifications. */}
              <Button
                variant="ghost"
                size="icon"
                aria-label="Search"
                className="text-white hover:bg-white/15 hover:text-white sm:hidden"
                onClick={() => setMobileSearchOpen(true)}
              >
                <Search className="h-5 w-5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                aria-label={
                  counts?.notifications
                    ? `Notifications, ${counts.notifications} unread`
                    : 'Notifications'
                }
                className="relative text-white hover:bg-white/15 hover:text-white"
                onClick={() => navigate('/dashboard/notifications')}
              >
                <Bell className="h-5 w-5" />
                <DotBadge count={counts?.notifications} />
              </Button>
              <TopbarPokes />
              {/* Mail / Messages icon — hidden on mobile per design 2026-06-08. */}
              <Button
                variant="ghost"
                size="icon"
                aria-label={
                  counts?.messages
                    ? `Messages, ${counts.messages} unread`
                    : 'Messages'
                }
                className="relative hidden text-white hover:bg-white/15 hover:text-white sm:inline-flex"
                onClick={() => navigate('/dashboard/messages')}
              >
                <Mail className="h-5 w-5" />
                <DotBadge count={counts?.messages} />
              </Button>
              <UserMenu />
            </div>
          </>
        )}
      </div>
    </header>
  );
}
